import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, ExternalLink } from "lucide-react";

// 2026-09-12 — plain-language, step-by-step Zerodha (Kite Connect) setup
// guide (chat: "prepare separate page on How to configure in Zerodha with
// steps for users ... assuming users are not tech-savvy) with link in
// settings page"). Linked from Settings.jsx's "How do I get these?" /
// "the step-by-step guide" links next to the Zerodha API Credentials
// section. No data fetching — this is pure reference content, styled to
// match the rest of the app (border-grid boxes, font-mono-data labels).

const Step = ({ n, title, children }) => (
  <div className="border border-grid p-5 space-y-2" data-testid={`zerodha-guide-step-${n}`}>
    <div className="flex items-center gap-3">
      <span
        className="flex items-center justify-center w-7 h-7 shrink-0 border font-mono-data text-sm"
        style={{ borderColor: "var(--bull)", color: "var(--bull)" }}
      >
        {n}
      </span>
      <h3 className="font-heading text-lg">{title}</h3>
    </div>
    <div className="text-sm text-muted-2 leading-relaxed pl-10 space-y-2">{children}</div>
  </div>
);

const Field = ({ children }) => (
  <span className="font-mono-data text-xs px-1.5 py-0.5 border border-grid" style={{ color: "var(--text-primary)" }}>
    {children}
  </span>
);

// 2026-09-20 — inline emphasis for a phrase that needs to stand out mid-
// paragraph (chat: "these words need to be highlighted", pointing at Step
// 4's "the important part for StoxTrade is that secret key text — copy
// it"). A tinted <mark> rather than another bold/italic <b>/<i> — the
// surrounding sentence already uses both, so a third kind of emphasis was
// needed to actually draw the eye.
const Highlight = ({ children }) => (
  <mark
    className="px-1 py-0.5"
    style={{ background: "rgba(96, 200, 130, 0.18)", color: "var(--bull)" }}
  >
    {children}
  </mark>
);

const ExtLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="underline underline-offset-2 inline-flex items-center gap-1"
    style={{ color: "var(--bull)" }}
  >
    {children} <ExternalLink size={11} />
  </a>
);

export const ZerodhaSetupGuide = () => {
  return (
    <section className="space-y-6 max-w-3xl" data-testid="zerodha-setup-guide-page">
      <div>
        <Link
          to="/settings"
          className="text-[10px] uppercase tracking-[0.2em] font-mono-data inline-flex items-center gap-1.5 mb-3"
          style={{ color: "var(--text-muted-2)" }}
        >
          <ArrowLeft size={12} /> Back to Settings
        </Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Settings · Zerodha
        </div>
        <h2 className="font-heading text-2xl">How to Connect Your Zerodha Account</h2>
        <p className="text-sm text-muted-2 leading-relaxed mt-2">
          This walks through getting the six pieces of information StoxTrade's Zerodha
          section on the Settings page asks for. None of it requires any coding —
          it's all clicking through Zerodha's own website. Budget about 10 minutes.
        </p>
      </div>

      <div className="border p-4 flex gap-3 items-start" style={{ borderColor: "var(--bear)" }}>
        <ShieldAlert size={18} style={{ color: "var(--bear)" }} className="shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed" style={{ color: "var(--text-primary)" }}>
          <strong>Treat everything below like your online banking password.</strong>{" "}
          Your API Secret, TOTP Secret and Zerodha Password can be used to access your
          real Zerodha account and place real trades. Only ever enter them into
          StoxTrade's own Settings page (never into an email, chat message, or any
          other website) — and never share them with anyone, including someone
          claiming to be StoxTrade support.
        </p>
      </div>

      <Step n={1} title="Create a free Kite Connect developer account">
        <p>
          Go to <ExtLink href="https://developers.kite.trade/login">developers.kite.trade</ExtLink> and
          sign in with your normal Zerodha login (same username/password you use for the
          Kite app). This is Zerodha's own developer site — it's free to sign up for.
        </p>
      </Step>

      <Step n={2} title="Create a new &ldquo;app&rdquo;">
        <p>
          Once signed in, look for <Field>My Apps</Field> → <Field>Create new app</Field>.
          Fill in:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Type of app:</b> Connect</li>
          <li><b>App name:</b> anything you'll recognise later, e.g. "StoxTrade"</li>
          <li><b>Zerodha Client ID:</b> your own Zerodha login ID (see Step 5 below if you're not sure what this is)</li>
          <li>
            <b>Redirect URL:</b> paste exactly <Field>https://stoxtrade.tech/api/kite/callback</Field>{" "}
            — this must match exactly, including <Field>https://</Field> at the start
          </li>
        </ul>
        <p>Save the app.</p>
      </Step>

      <Step n={3} title="Copy your API Key and API Secret">
        <p>
          After the app is created, Zerodha's page shows an <b>API Key</b> and lets you
          reveal/generate an <b>API Secret</b>. Copy both somewhere safe for a moment —
          you'll paste them into StoxTrade's Settings page in Step 7.
        </p>
        <p className="text-[11px] text-muted-3">
          Note on cost: Zerodha offers a free "Personal" plan (order placement and
          portfolio tracking, no live market-data feed) and a paid "Connect" plan
          (currently ₹500/month per API key, adds live market data). Check{" "}
          <ExtLink href="https://kite.trade/">kite.trade</ExtLink> for Zerodha's current
          pricing before choosing — if you're unsure which one you need, the free
          Personal plan is the safer starting point and can be upgraded later.
        </p>
      </Step>

      <Step n={4} title="Turn on TOTP (authenticator app) login for your Zerodha account">
        <p>
          Open the Kite app or <ExtLink href="https://kite.zerodha.com">kite.zerodha.com</ExtLink> and
          sign in normally. Then go to your profile icon → <Field>Password &amp; Security</Field>,
          and look for <Field>External TOTP</Field> (sometimes called Time-based OTP or
          2FA). Choose to set it up with an external authenticator app.
        </p>
        <p>
          Zerodha will show you a QR code <i>and</i>, right next to or below it, a line of
          letters and numbers called the <b>secret key</b>. You can scan the QR code into
          an app like Google Authenticator if you also want it there, but{" "}
          <Highlight>
            the important part for StoxTrade is that secret key text — copy it. This is
            what goes into the "TOTP Secret" field in Step 7.
          </Highlight>
        </p>
        <p className="text-[11px] text-muted-3">
          Zerodha only shows this secret key once, at setup time — if you miss it,
          you'll need to reset/reconfigure TOTP to see it again.
        </p>
      </Step>

      <Step n={5} title="Find your Zerodha Client / User ID">
        <p>
          This is shown at the top of the Kite app or website once you're logged in —
          a short code like <Field>AB1234</Field>. It's the same ID you used as your
          username to sign in, and the same one you entered as "Zerodha Client ID"
          back in Step 2.
        </p>
      </Step>

      <Step n={6} title="Have your Zerodha password ready">
        <p>
          This is the same password you normally use to log into Kite. StoxTrade needs
          it for the "Zerodha Password" field on the Settings page.
        </p>
      </Step>

      <Step n={7} title="Enter everything into StoxTrade">
        <p>Go back to <Link to="/settings" className="underline underline-offset-2" style={{ color: "var(--bull)" }}>Settings</Link> and, under
          <b> Zerodha API Credentials</b>, fill in the six fields:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>API Key</b> — from Step 3</li>
          <li><b>API Secret</b> — from Step 3</li>
          <li><b>Redirect URL</b> — the same value you entered in Step 2: <Field>https://stoxtrade.tech/api/kite/callback</Field></li>
          <li><b>TOTP Secret</b> — the secret key from Step 4</li>
          <li><b>Zerodha Client / User ID</b> — from Step 5</li>
          <li><b>Zerodha Password</b> — from Step 6</li>
        </ul>
        <p>Click <b>Save Zerodha Credentials</b>, then click <b>Test Connection</b> to confirm everything is working.</p>
      </Step>

      <div className="border border-grid p-5 space-y-2">
        <h3 className="font-heading text-lg">If something doesn't work</h3>
        <ul className="text-sm text-muted-2 leading-relaxed list-disc pl-5 space-y-1.5">
          <li>
            <b>"Test Connection" fails:</b> double-check the Redirect URL matches
            exactly (Step 2 and Step 7 must be identical, letter for letter), and that
            you copied the API Secret without extra spaces.
          </li>
          <li>
            <b>TOTP Secret doesn't look right:</b> go back into Zerodha's Password &amp;
            Security page and reset External TOTP to get a fresh secret key — the old
            one stops working once you do this.
          </li>
          <li>
            <b>Still stuck:</b> reach out to StoxTrade support with a description of
            which step failed — never send your actual password, API Secret, or TOTP
            Secret in a support message.
          </li>
        </ul>
      </div>
    </section>
  );
};

export default ZerodhaSetupGuide;
